import { ApiError } from '@/api/envelope';

/**
 * The backend's code for an organization view it cannot have: the global
 * handler's, never a screen's (PNF5).
 */
export const ORGANIZATION_CONTEXT = 'organization_context_unavailable';

/**
 * What a refused request means to a screen (`docs/error-contract.md`, P21):
 * `outside` is the session's or the organization context's, which no screen
 * shows (PNF5); `refused` is any other 4xx in the backend's words; `failed`
 * is a server failure or no answer at all.
 */
export type RefusalKind =
  'outside' | 'notFound' | 'forbidden' | 'conflict' | 'invalid' | 'refused' | 'failed';

/** Sorts a refused request once, so every screen routes the statuses alike. */
export function refusalOf(error: unknown): { kind: RefusalKind; sentence: string } {
  if (!(error instanceof ApiError)) return { kind: 'failed', sentence: '' };
  const sentence = error.serverMessage ?? error.message;
  const as = (kind: RefusalKind) => ({ kind, sentence });
  if (error.status === 401) return as('outside');
  if (error.status === 409 && error.serverMessage === ORGANIZATION_CONTEXT) {
    return as('outside');
  }
  if (error.status === 404) return as('notFound');
  if (error.status === 403) return as('forbidden');
  if (error.status === 409) return as('conflict');
  if (error.status === 422) return as('invalid');
  if (error.status < 500) return as('refused');
  return as('failed');
}

/** Where a save's refusal is shown: a field, a panel, or (null) the alert. */
export type PageRefusedField =
  'title' | 'summary' | 'alt' | 'headerImage' | 'content' | 'links';

/*
 * The backend's sentences (`pages/service.py`, `rich_text.py`), matched by
 * their words, in this order: the rich-text ones first, because "A link's
 * address ..." is about the content, not about Linked Content.
 */
const RICH_TEXT = [
  /^Rich text /,
  /^The text has /,
  /call-to-action button/i,
  /^A button /,
  /^An image /,
  /^A link'?s? address/,
  /^This image is not one of this organization's/,
];
const ALT = /^The header image description/;
const HEADER = /^The header image /;
const HELD_ELSEWHERE = /^This image belongs to another page/;
const TITLE = /^(The title |A page needs a title$)/;
const SUMMARY = /^The summary /;
const LINKS = [
  /^Linking /,
  / is linked twice$/,
  /can link at most /,
  /^The (document|campaign|petition) .+ is not one of this organization's$/,
];

/**
 * The field a 422 from a create or a save names, read from the backend's own
 * sentence, which `applyServerErrors` cannot read as labelled (design.md
 * "Where each refusal goes"), as `documentFieldRefusal` does for documents.
 * Null leaves it to the caller: the publish rule names several parts, and
 * anything unknown is said whole in the alert. "This image belongs to
 * another page" is the header image's when the body named one, else an
 * inline image's (backend D11).
 */
export function pageFieldRefusal(
  error: unknown,
  { headerSent }: { headerSent: boolean },
): { field: PageRefusedField; message: string } | null {
  if (!(error instanceof ApiError) || error.status !== 422 || !error.serverMessage) {
    return null;
  }
  const message = error.serverMessage;
  const on = (field: PageRefusedField) => ({ field, message });

  if (RICH_TEXT.some((pattern) => pattern.test(message))) return on('content');
  if (ALT.test(message)) return on('alt');
  if (HEADER.test(message)) return on('headerImage');
  if (HELD_ELSEWHERE.test(message)) return on(headerSent ? 'headerImage' : 'content');
  if (TITLE.test(message)) return on('title');
  if (SUMMARY.test(message)) return on('summary');
  if (LINKS.some((pattern) => pattern.test(message))) return on('links');
  return null;
}
